import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router';
import { NavRibbon, NavRibbonLink } from '../../../../components/nav-ribbon/NavRibbon';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { hasPermission } from '../../../../containers/authorization-helpers/has-permission';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { IApplicationState } from '../../../../store/IApplicationState';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { updateSelectedProductionPlanningTab } from '../../../../production/actions';
import '../../../../production/styles.scss';
import { ProductionPlanningShipmentsScene } from './PlanningShipments';
import { ProductionPlanningOrdersScene } from './PlanningOrders';

export enum ProductionPlanningHeaderScenario {
    None,
    PickupExport,
    DeliveryExport
}

export const ProductionPlanningScene = (props: IBaseSceneProps) => {

    const dispatch = useDispatch();
    const storedPath = useSelector((state : IApplicationState) => state.production.selectedPlanningTab);
    const [currentLocation, setCurrentLocation] = React.useState('');

    // Action Related
    const [scenario, setScenario] = React.useState<ProductionPlanningHeaderScenario>();
    const [pickupExportFlag, setPickupExportFlag] = React.useState(0);
    const [createFromOrderIdsFlag, setCreateFromOrderIdsFlag] = React.useState(0);
    const [deliveryExportFlag, setDeliveryExportFlag] = React.useState(0);
    
    // We want to track the current tab we're showing
    React.useEffect(() => {
        if (props.location.pathname !== currentLocation) {
            _persistTab();            
        }
    }, [props.location]);

    const setButtonState = (sce : ProductionPlanningHeaderScenario) => {
        setScenario(sce);        
    };

    /**
     * This handles checking the current user permissions to determine what the default 
     * path should be
     */
    const getRedirectPath = () => {

        const pPerm = Permissions.ProductionMetricsPickupsView;
        const dPerm = Permissions.ProductionMetricsDeliveriesView;
        
        if (hasPermission({ requiredPermissions: [dPerm]})) {
            return Routes.PRODUCTION_PLANNING_SHIPMENTS;
        }
        else if (hasPermission({ requiredPermissions: [pPerm]})) {
            return Routes.PRODUCTION_PLANNING_ORDERS;
        }

        return '/';
    };

    const getButtons = () => {
        if (scenario === ProductionPlanningHeaderScenario.PickupExport) {
            return (
                <HasPermission requiredPermissions={[
                    Permissions.ProductionMetricsCreateRun
                ]}>
                    <button 
                        onClick={ () => setCreateFromOrderIdsFlag(createFromOrderIdsFlag + 1) }
                        className="btn new attention">
                        Create
                    </button>
                    <button 
                        onClick={ () => setPickupExportFlag(pickupExportFlag + 1) }
                        className="btn new attention">
                        Export
                    </button>
                </HasPermission>
            );
        }
        else if (scenario === ProductionPlanningHeaderScenario.DeliveryExport) {
            return (
                <HasPermission requiredPermissions={[
                    Permissions.ProductionMetricsCreateRun
                ]}>
                    <button 
                        onClick={ () => setDeliveryExportFlag(deliveryExportFlag + 1) }
                        className="btn new attention auto-width">
                        Export
                    </button>
                </HasPermission>
            );
        }        
        else {
            return null;
        }
    };

    const _persistTab = () => {

        setCurrentLocation(props.location.pathname);
        
        let p = '';
        if (document.location.pathname.endsWith(Routes.PRODUCTION_PLANNING_SHIPMENTS)) {            
            p = Routes.PRODUCTION_PLANNING_SHIPMENTS;
            setButtonState(ProductionPlanningHeaderScenario.DeliveryExport);
        }
        else if (document.location.pathname.endsWith(Routes.PRODUCTION_PLANNING_ORDERS)) {
            p = Routes.PRODUCTION_PLANNING_ORDERS;
            setButtonState(ProductionPlanningHeaderScenario.PickupExport);
        }
        else if (storedPath) {
            p = storedPath;
        }

        dispatch(updateSelectedProductionPlanningTab(p));
    };

    return <PageContent pageName={PageNames.PRODUCTION_PLANNING}>
            <PageHeading shouldStick={ true }>
                <div className="metrics-button-list">{ getButtons() }</div>
            </PageHeading>

            <NavRibbon links={[
                new NavRibbonLink(
                    Routes.PRODUCTION_PLANNING_ORDERS,
                    'la-list',
                    'Orders',
                    [Permissions.ProductionMetricsPickupsView]
                ),
                new NavRibbonLink(
                    Routes.PRODUCTION_PLANNING_SHIPMENTS,
                    'la-truck',
                    'Shipments',
                    [
                        Permissions.ProductionMetricsDeliveriesView
                    ]
                )
            ]} />

            <Switch>

                { /* Pickup Planning */ }
                <Route
                    key="production-planning-pickup"
                    exact={ true }
                    path={ Routes.PRODUCTION_PLANNING_ORDERS }
                    render={() => 
                        <ProductionPlanningOrdersScene 
                            setButtonState={ setButtonState }
                            exportFlag={ pickupExportFlag }
                            createFromOrderIdsFlag={ createFromOrderIdsFlag }
                            {...props} 
                        />
                    }
                />

            { /* Delivery Planning */ }
                <Route
                    key="production-planning-deliveries"
                    exact={true}
                    path={ Routes.PRODUCTION_PLANNING_SHIPMENTS }
                    render={() => 
                        <ProductionPlanningShipmentsScene 
                            setButtonState={ setButtonState }
                            exportFlag={ deliveryExportFlag }
                            {...props} 
                        />
                    }
                />

                <Redirect
                    key="production-planning-redirect"
                    exact={true}
                    from={ Routes.PRODUCTION_PLANNING }
                    to={ storedPath ? storedPath : getRedirectPath() } />

            </Switch>
            
        </PageContent>;
    
};
